<template>
  <div class="save-retention-options">
    <button
      type="button"
      class="save-retention-options__trigger"
      @click="toggleExpanded"
    >
      <div class="save-retention-options__trigger-copy">
        <h6 class="save-retention-options__title">{{ title }}</h6>
        <div class="save-retention-options__summary">{{ activeOptionsCount }} aktiv</div>
      </div>
      <div class="save-retention-options__trigger-icon" :class="{ 'save-retention-options__trigger-icon--open': expanded }">
        <CIcon name="cil-chevron-bottom"/>
      </div>
    </button>

    <transition name="save-retention-options-collapse">
      <div v-show="expanded" class="save-retention-options__content">
        <div class="save-retention-options__persist form-group">
          <CSwitch
            color="success"
            :checked="persistValue"
            @update:checked="updatePersistValue"
          />
          <div class="save-retention-options__persist-copy px-3">
            <div class="save-retention-options__persist-label">{{ persistLabel }}</div>
            <div class="save-retention-options__persist-hint">{{ persistHint }}</div>
          </div>
        </div>
        <div class="save-retention-options__list">
          <button
            v-for="option in options"
            :key="option.key"
            type="button"
            class="save-retention-options__item form-group"
            @click="toggleOption(option.key)"
          >
            <div class="save-retention-options__item-copy">
              <span class="save-retention-options__item-label">{{ option.label }}</span>
            </div>
            <div class="save-retention-options__item-switch" @click.stop>
              <CSwitch
                color="success"
                :checked="Boolean(value[option.key])"
                @update:checked="updateOption(option.key, $event)"
              />
            </div>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SaveRetentionOptions',
  props: {
    options: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: 'Nach dem Speichern behalten'
    },
    persistLabel: {
      type: String,
      default: 'Auswahl merken'
    },
    persistHint: {
      type: String,
      default: 'Ohne Merken werden die Behalten-Optionen wieder auf den Standard zurückgesetzt. Das ist hilfreich beim Nachtragen mehrerer Einträge hintereinander.'
    },
    persistValue: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      expanded: false
    }
  },
  computed: {
    activeOptionsCount () {
      return this.options.filter((option) => Boolean(this.value[option.key])).length
    }
  },
  methods: {
    toggleExpanded () {
      this.expanded = !this.expanded
    },
    updatePersistValue (checked) {
      this.$emit('update:persistValue', checked)
    },
    updateOption (key, checked) {
      this.$emit('input', {
        ...this.value,
        [key]: checked
      })
    },
    toggleOption (key) {
      this.updateOption(key, !this.value[key])
    }
  }
}
</script>

<style lang="scss">
.save-retention-options {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  margin-top: 1rem;
  overflow: hidden;
}

.save-retention-options__trigger {
  align-items: center;
  appearance: none;
  background: transparent;
  border: 0;
  display: flex;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  text-align: left;
  width: 100%;
}

.save-retention-options__trigger-copy {
  min-width: 0;
}

.save-retention-options__title {
  color: #1f2933;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.1rem;
}

.save-retention-options__summary {
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.3;
}

.save-retention-options__trigger-icon {
  align-items: center;
  color: #6c757d;
  display: inline-flex;
  justify-content: center;
  min-width: 2rem;
  transition: transform 0.18s ease;
}

.save-retention-options__trigger-icon--open {
  transform: rotate(180deg);
}

.save-retention-options__content {
  border-top: 1px solid #e8ebef;
  padding: 0.85rem 1rem 1rem;
}

.save-retention-options__persist {
  align-items: center;
  background: #f5f6f8;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  display: flex;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
}

.save-retention-options__persist-copy {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.save-retention-options__persist-label {
  color: #1f2933;
  font-weight: 600;
  line-height: 1.2;
}

.save-retention-options__persist-hint {
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.35;
}

.save-retention-options__list {
  display: grid;
  gap: 0.5rem 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.save-retention-options__item {
  align-items: center;
  appearance: none;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  min-height: 3rem;
  padding: 0.65rem 0.85rem;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  width: 100%;
}

.save-retention-options__item:hover,
.save-retention-options__item:focus {
  background: #f8fafc;
  border-color: #c7d0db;
  outline: none;
}

.save-retention-options__item-copy {
  padding-right: 0.75rem;
}

.save-retention-options__item-label {
  color: #24303d;
  font-weight: 600;
  line-height: 1.3;
}

.save-retention-options__item-switch {
  flex: 0 0 auto;
}

.save-retention-options-collapse-enter-active,
.save-retention-options-collapse-leave-active {
  transition: opacity 0.18s ease;
}

.save-retention-options-collapse-enter,
.save-retention-options-collapse-leave-to {
  opacity: 0;
}

@media (max-width: 575.98px) {
  .save-retention-options__content {
    padding: 0.75rem 0.85rem 0.85rem;
  }

  .save-retention-options__trigger {
    padding: 0.8rem 0.85rem;
  }

  .save-retention-options__list {
    grid-template-columns: 1fr;
  }

  .save-retention-options__persist {
    align-items: flex-start;
  }
}
</style>
